/**
 * src/pages/GlossaryAndReferences/sections/GlossarySection.tsx
 * The interactive glossary component featuring:
 * - Search by term and definition text
 * - Dynamic A-Z jump strip (only existing initial letters)
 * - Category filter pills
 * - URL hash target detection with scroll via scrollToElementAfterRender
 * - In-page jump support for related terms
 * - Epistemic status badges
 * - "Explained in" links
 * - Clean zero-results fallback state
 */

import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "@/components/common/Link";
import { StatusBadge } from "@/components/knowledge/StatusBadge";
import { GLOSSARY, type GlossaryCategory, type GlossaryEntry } from "@/lib/knowledge/glossary";
import { EXTRA_GLOSSARY } from "@/lib/knowledge/glossaryExtra";
import { scrollToElementAfterRender } from "@/lib/scroll";
import { Search, X, CornerDownRight, ExternalLink, ArrowRight, BookOpen } from "lucide-react";
import "../glossaryReferences.css";
const ALL_TERMS: GlossaryEntry[] = [...GLOSSARY, ...EXTRA_GLOSSARY];
const CATEGORIES: {
  id: GlossaryCategory | "all";
  label: string;
}[] = [{
  id: "all",
  label: "All Disciplines"
}, {
  id: "epistemology",
  label: "Epistemology"
}, {
  id: "logic",
  label: "Formal Logic"
}, {
  id: "philosophy",
  label: "Philosophy"
}, {
  id: "reasoning",
  label: "Critical Reasoning"
}, {
  id: "science",
  label: "Scientific Method"
}];
export function GlossarySection() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<GlossaryCategory | "all">("all");
  const [activeLetter, setActiveLetter] = useState<string | null>(null);
  const [targetedTermId, setTargetedTermId] = useState<string | null>(null);

  // Handle URL hash target highlighting & auto-scroll
  const handleHashChange = () => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash;
    if (hash && hash.startsWith("#term-")) {
      const termId = hash.replace("#term-", "");
      setTargetedTermId(termId);
      // Ensure the entry is visible by clearing search if necessary
      scrollToElementAfterRender(() => document.getElementById(`term-${termId}`), {
        offset: 100,
        behavior: "smooth"
      });
    } else {
      setTargetedTermId(null);
    }
  };
  useEffect(() => {
    handleHashChange();
    window.addEventListener("hashchange", handleHashChange);
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  // Filtered glossary entries based on search and category
  const filteredEntries = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return ALL_TERMS.filter(entry => {
      const matchesCategory = selectedCategory === "all" || entry.category === selectedCategory;
      if (!matchesCategory) return false;
      if (!q) return true;
      return entry.term.toLowerCase().includes(q) || entry.definition.toLowerCase().includes(q) || entry.example && entry.example.toLowerCase().includes(q);
    });
  }, [searchQuery, selectedCategory]);

  // Compute distinct existing starting letters from the filtered entries (or all entries)
  const availableLetters = useMemo(() => {
    const letters = new Set<string>();
    ALL_TERMS.forEach(entry => {
      const firstChar = entry.term.charAt(0).toUpperCase();
      if (/[A-Z]/.test(firstChar)) {
        letters.add(firstChar);
      }
    });
    return Array.from(letters).sort();
  }, []);

  // Jump to letter anchor or filter
  const scrollToLetter = (letter: string) => {
    setActiveLetter(letter);
    // Find first term in current list starting with letter
    const match = filteredEntries.find(e => e.term.charAt(0).toUpperCase() === letter);
    if (match) {
      scrollToElementAfterRender(() => document.getElementById(`term-${match.id}`), {
        offset: 100
      });
    } else {
      // If no matching term in current filter, reset search/category to reveal it
      setSelectedCategory("all");
      setSearchQuery("");
      setTimeout(() => {
        const globalMatch = ALL_TERMS.find(e => e.term.charAt(0).toUpperCase() === letter);
        if (globalMatch) {
          scrollToElementAfterRender(() => document.getElementById(`term-${globalMatch.id}`), {
            offset: 100
          });
        }
      }, 50);
    }
  };
  return <section id="glossary-archive" className="w-full max-w-[76rem] mx-auto px-4 sm:px-6 lg:px-8 py-8 min-w-0">
      {/* Section Header */}
      <div className="mb-8">
        <span className="ql-eyebrow">Taxonomy & Lexicon</span>
        <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mt-2 mb-3">
          Glossary of Rational Concepts
        </h2>
        <p className="font-sans text-sm sm:text-base text-muted-foreground max-w-3xl leading-relaxed">
          Search over 70 formal definitions across propositional logic, epistemology, cognitive science, and philosophical inquiry. Each entry specifies precise logical definitions, counterexamples, and connected laboratories.
        </p>
      </div>

      {/* Filter and Search Bar Controls */}
      <div className="space-y-4 mb-8">
        {/* Search Input Row */}
        <div className="relative w-full min-w-0">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" aria-hidden="true" />
                  <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search by concept, definition, or example (e.g. 'Gettier', 'validity', 'fallacy')..." aria-label="Search glossary terms" className="gl-search-input text-base sm:text-sm" />
          {searchQuery && <button type="button" onClick={() => setSearchQuery("")} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1 rounded min-w-[32px] min-h-[32px] flex items-center justify-center cursor-pointer" aria-label="Clear search">
              <X className="size-4" aria-hidden="true" />
            </button>}
        </div>

        {/* Category Filter Pills */}
        <div className="flex flex-wrap gap-2 items-center min-w-0" role="group" aria-label="Filter terms by category">
          {CATEGORIES.map(cat => {
          const isSelected = selectedCategory === cat.id;
          return <button key={cat.id} type="button" onClick={() => setSelectedCategory(cat.id)} aria-pressed={isSelected} className={`gl-filter-pill ${isSelected ? "gl-filter-pill-selected" : ""}`}>
                <span>{cat.label}</span>
              </button>;
        })}
        </div>

        {/* Alphabetical Jump Strip with accessible scroll region */}
        <div className="gl-az-strip min-w-0 max-w-full overflow-x-auto" aria-label="Alphabetical term navigation" tabIndex={0}>
          <span className="font-mono text-xs text-muted-foreground mr-1 uppercase shrink-0">A–Z:</span>
          {availableLetters.map(letter => {
          const isActive = activeLetter === letter;
          return <button key={letter} type="button" onClick={() => scrollToLetter(letter)} aria-label={`Jump to letter ${letter}`} className={`gl-az-button shrink-0 ${isActive ? "gl-az-button-active" : ""}`}>
                {letter}
              </button>;
        })}
        </div>
      </div>

      {/* Results Count Telemetry */}
      <div className="flex items-center justify-between gap-4 mb-6 font-mono text-xs text-muted-foreground border-b border-border/40 pb-3">
        <span>
          Showing <strong className="text-foreground">{filteredEntries.length}</strong> of {ALL_TERMS.length} terms
        </span>
        {searchQuery && <span>
            Filter active: &ldquo;{searchQuery}&rdquo;
          </span>}
      </div>

      {/* Entries Grid or Zero State */}
      {filteredEntries.length === 0 ? <div className="ql-glass p-8 sm:p-12 rounded-xl text-center border border-border/60 my-8">
          <BookOpen className="size-8 text-muted-foreground mx-auto mb-3" aria-hidden="true" />
          <h3 className="font-serif text-lg font-bold text-foreground mb-2">No Matching Terms Found</h3>
          <p className="font-sans text-sm text-muted-foreground max-w-md mx-auto mb-6">
            No glossary concept matches &ldquo;{searchQuery}&rdquo; within the selected discipline. Try resetting your search query or choosing &ldquo;All Disciplines&rdquo;.
          </p>
          <button type="button" onClick={() => {
        setSearchQuery("");
        setSelectedCategory("all");
      }} className="ql-btn-neon inline-flex items-center gap-2">
            <span>Reset All Filters</span>
          </button>
        </div> : <div className="grid grid-cols-1 md:grid-cols-2 gap-5 min-w-0">
          {filteredEntries.map(entry => {
        const isTargeted = targetedTermId === entry.id;
        return <article key={entry.id} id={`term-${entry.id}`} className={`gl-entry-card ${isTargeted ? "gl-entry-target" : ""}`} aria-labelledby={`heading-term-${entry.id}`}>
                {/* Header row: Term title and status badge */}
                <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                  <h3 id={`heading-term-${entry.id}`} className="font-serif text-lg sm:text-xl font-bold text-foreground tracking-tight">
                    {entry.term}
                  </h3>
                  {entry.status && <StatusBadge status={entry.status} compact />}
                </div>

                {/* Definition prose */}
                <p className="font-sans text-sm sm:text-base text-foreground/90 leading-relaxed mb-4 break-words">
                  {entry.definition}
                </p>

                {/* Illustrative Example */}
                {entry.example && <div className="p-3 rounded-lg bg-card/40 border border-border/40 text-xs sm:text-sm text-muted-foreground mb-4 font-sans">
                    <span className="font-mono text-[10px] uppercase font-bold text-primary block mb-1">
                      Illustrative Demonstration
                    </span>
                    <p className="leading-relaxed break-words italic">
                      &ldquo;{entry.example}&rdquo;
                    </p>
                  </div>}

                {/* Related Terms (in-page anchor links) */}
                {entry.related && entry.related.length > 0 && <div className="mb-4">
                    <span className="font-mono text-[11px] text-muted-foreground uppercase tracking-wider block mb-1.5">
                      Related Concepts:
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {entry.related.map(relId => {
                const relEntry = ALL_TERMS.find(g => g.id === relId);
                const label = relEntry ? relEntry.term : relId;
                return <Link key={relId} to={`#term-${relId}`} className="gl-tag-link" title={`Jump to ${label}`}>
                            <CornerDownRight className="size-3 text-primary shrink-0" aria-hidden="true" />
                            <span>{label}</span>
                          </Link>;
              })}
                    </div>
                  </div>}

                {/* See Also / Explained in cross-links */}
                {entry.seeAlso && entry.seeAlso.length > 0 && <div className="pt-3 border-t border-border/40 flex items-center gap-2 flex-wrap text-xs">
                    <span className="font-mono text-[11px] text-muted-foreground uppercase tracking-wider">
                      Tested In:
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {entry.seeAlso.map((item, idx) => <Link key={idx} to={item.to} className="inline-flex items-center gap-1 text-primary hover:underline font-mono text-xs">
                          <span>{item.label}</span>
                          <ArrowRight className="size-3 shrink-0" aria-hidden="true" />
                        </Link>)}
                    </div>
                  </div>}
              </article>;
      })}
        </div>}
    </section>;
}